import type { League, LeagueSquad, Player, SquadPlayer, TransferHistoryEntry } from '../types/database';
import type { TransferData } from '../components/squad/TransferModal';
import { performAutoSlot, countOverseasInMainSquad } from './slotManagement';
import {
  calculatePlayerContribution,
  calculateSquadPoints as calculateSquadPointsUtil,
} from './pointsCalculation';

/**
 * Pure orchestration for a cricket transfer — the single source of truth for
 * banking, role swapping, auto-slotting, squad-rule validation, the
 * point-stability guards (PT-001 / PT-002), counter increments and the
 * transferHistory entry.
 *
 * This was extracted verbatim from SquadSelectionPage.handleTransferSubmit so
 * the player-facing page and the AdminCricketTransferPanel share ONE
 * implementation and can never drift. The function performs NO I/O: it returns
 * the Firestore update payload, and the caller is responsible for writing it
 * (and for refreshing the leaderboard snapshot).
 *
 * The only behavioural difference between the two callers is `actedByAdminUid`:
 * when the admin acts on a player's behalf, the resulting transferHistory entry
 * is stamped with the admin's UID for audit. Everything else — including the
 * point math — is identical.
 */

export interface ComputeCricketTransferParams {
  existingSquad: LeagueSquad;
  transferData: TransferData;
  availablePlayers: Player[];
  league: League;
  /** When set, marks the transferHistory entry as admin-initiated on behalf of the owner. */
  actedByAdminUid?: string;
}

export interface CricketTransferResult {
  /** Ready to pass straight to squadService.update(existingSquad.id, …). */
  updatePayload: Record<string, unknown>;
  pointsBefore: number;
  pointsAfter: number;
  /** Points banked by this transfer (the delta added to bankedPoints). */
  bankedDelta: number;
}

/**
 * Build a SquadPlayer for a player joining via transfer. Inlined from
 * squadPlayerUtils.createTransferSquadPlayer so this util stays free of any
 * Firebase import and remains trivially unit-testable.
 */
function makeTransferSquadPlayer(player: {
  playerId: string;
  playerName: string;
  team: string;
  role: SquadPlayer['role'];
  points: number;
  isOverseas?: boolean;
}): SquadPlayer {
  const squadPlayer: SquadPlayer = {
    playerId: player.playerId,
    playerName: player.playerName,
    team: player.team,
    role: player.role,
    points: player.points,
    matchPerformances: {},
    addedAt: new Date(),
    pointsAtJoining: player.points, // Snapshot current points - only future gains count
  };
  if (player.isOverseas !== undefined) {
    squadPlayer.isOverseas = player.isOverseas;
  }
  return squadPlayer;
}

export function computeCricketTransferUpdate(
  params: ComputeCricketTransferParams,
): CricketTransferResult {
  const { existingSquad, transferData, availablePlayers, league, actedByAdminUid } = params;

  const calculateSquadPoints = (
    players: SquadPlayer[],
    captainId: string | null,
    viceCaptainId: string | null,
    xFactorId: string | null,
    bankedPoints = 0,
  ) =>
    calculateSquadPointsUtil(
      players,
      league.squadSize || 11,
      captainId || undefined,
      viceCaptainId || undefined,
      xFactorId || undefined,
      bankedPoints,
    );

  // CRITICAL: Create a DEEP copy of players array to avoid mutating original objects
  // Shallow copy [...array] only copies the array, not the objects inside!
  let updatedPlayers: SquadPlayer[] = existingSquad.players.map((player) => {
    const poolPlayer = availablePlayers.find((ap) => ap.id === player.playerId);
    const playerCopy: SquadPlayer = {
      ...player,
      pointsAtJoining: player.pointsAtJoining ?? 0,
      // Resolve isOverseas from live player pool as authoritative source
      isOverseas: poolPlayer ? poolPlayer.isOverseas : (player.isOverseas ?? false),
    };

    // Always preserve pointsWhenRoleAssigned if it exists; never auto-set it.
    if (player.pointsWhenRoleAssigned !== undefined) {
      playerCopy.pointsWhenRoleAssigned = player.pointsWhenRoleAssigned;
    }

    return playerCopy;
  });
  let updatedCaptainId = existingSquad.captainId;
  let updatedViceCaptainId = existingSquad.viceCaptainId;
  let updatedXFactorId = existingSquad.xFactorId;
  let additionalBankedPoints = 0; // Track points to bank from this transfer

  // Process the transfer based on change type
  if (
    transferData.changeType === 'playerSubstitution' &&
    transferData.playerOut &&
    transferData.playerIn
  ) {
    if (transferData.transferType === 'bench') {
      // BENCH TRANSFER: Swap a main squad player with a bench player
      const playerOutIndex = updatedPlayers.findIndex((p) => p.playerId === transferData.playerOut);
      const playerInIndex = updatedPlayers.findIndex((p) => p.playerId === transferData.playerIn);

      if (playerOutIndex === -1) {
        throw new Error('Transfer failed: The player you want to remove is no longer in your squad. Please refresh and try again.');
      }
      if (playerInIndex === -1) {
        throw new Error('Transfer failed: The bench player is no longer available. Please refresh and try again.');
      }

      // Calculate points to bank from the player moving to bench
      const playerMovingToBench = updatedPlayers[playerOutIndex];
      let playerRole: 'captain' | 'viceCaptain' | 'xFactor' | 'regular' = 'regular';
      if (playerMovingToBench.playerId === existingSquad.captainId) playerRole = 'captain';
      else if (playerMovingToBench.playerId === existingSquad.viceCaptainId) playerRole = 'viceCaptain';
      else if (playerMovingToBench.playerId === existingSquad.xFactorId) playerRole = 'xFactor';

      additionalBankedPoints = calculatePlayerContribution(playerMovingToBench, playerRole);

      // Swap the players - simple array swap
      const temp = updatedPlayers[playerOutIndex];
      updatedPlayers[playerOutIndex] = updatedPlayers[playerInIndex];
      updatedPlayers[playerInIndex] = temp;

      // Freeze transferred-out player's contribution at transfer time.
      const playerNowOnBench = updatedPlayers[playerInIndex];
      playerNowOnBench.pointsAtJoining = playerNowOnBench.points;
      playerNowOnBench.pointsWhenRoleAssigned = playerNowOnBench.points;

      // Reset pointsAtJoining for bench player moving to main squad
      const playerMovingToMain = updatedPlayers[playerOutIndex];
      playerMovingToMain.pointsAtJoining = playerMovingToMain.points;

      // AUTO-ASSIGN roles to incoming player if swapping out C/VC/X
      const incomingPlayerId = transferData.playerIn;

      if (transferData.playerOut === existingSquad.captainId) {
        if (incomingPlayerId === existingSquad.viceCaptainId) {
          updatedViceCaptainId = existingSquad.captainId;
        } else if (incomingPlayerId === existingSquad.xFactorId) {
          updatedXFactorId = existingSquad.captainId;
        }

        updatedCaptainId = incomingPlayerId;
        const newCaptain = updatedPlayers.find((p) => p.playerId === incomingPlayerId);
        if (newCaptain) {
          newCaptain.pointsAtJoining = newCaptain.points;
          newCaptain.pointsWhenRoleAssigned = newCaptain.points;
        }

        const oldCaptain = updatedPlayers.find((p) => p.playerId === existingSquad.captainId);
        if (oldCaptain) {
          oldCaptain.pointsAtJoining = oldCaptain.points;
          oldCaptain.pointsWhenRoleAssigned = oldCaptain.points;
        }
      }

      if (transferData.playerOut === existingSquad.viceCaptainId) {
        if (incomingPlayerId === existingSquad.captainId) {
          updatedCaptainId = existingSquad.viceCaptainId;
        } else if (incomingPlayerId === existingSquad.xFactorId) {
          updatedXFactorId = existingSquad.viceCaptainId;
        }

        updatedViceCaptainId = incomingPlayerId;
        const newVC = updatedPlayers.find((p) => p.playerId === incomingPlayerId);
        if (newVC) {
          newVC.pointsAtJoining = newVC.points;
          newVC.pointsWhenRoleAssigned = newVC.points;
        }

        const oldVC = updatedPlayers.find((p) => p.playerId === existingSquad.viceCaptainId);
        if (oldVC) {
          oldVC.pointsAtJoining = oldVC.points;
          oldVC.pointsWhenRoleAssigned = oldVC.points;
        }
      }

      if (transferData.playerOut === existingSquad.xFactorId) {
        if (incomingPlayerId === existingSquad.captainId) {
          updatedCaptainId = existingSquad.xFactorId;
        } else if (incomingPlayerId === existingSquad.viceCaptainId) {
          updatedViceCaptainId = existingSquad.xFactorId;
        }

        updatedXFactorId = incomingPlayerId;
        const newX = updatedPlayers.find((p) => p.playerId === incomingPlayerId);
        if (newX) {
          newX.pointsAtJoining = newX.points;
          newX.pointsWhenRoleAssigned = newX.points;
        }

        const oldX = updatedPlayers.find((p) => p.playerId === existingSquad.xFactorId);
        if (oldX) {
          oldX.pointsAtJoining = oldX.points;
          oldX.pointsWhenRoleAssigned = oldX.points;
        }
      }
    } else {
      // FLEXIBLE/MID-SEASON/WILDCARD TRANSFER: Replace with a player from pool OR bench
      const playerOutIndex = updatedPlayers.findIndex((p) => p.playerId === transferData.playerOut);
      if (playerOutIndex === -1) {
        throw new Error('Transfer failed: The player you want to remove is no longer in your squad. Please refresh and try again.');
      }

      // Check if incoming player is from bench
      const benchPlayerIds = updatedPlayers.slice(league.squadSize).map((p) => p.playerId);
      const isIncomingPlayerFromBench = benchPlayerIds.includes(transferData.playerIn);

      if (isIncomingPlayerFromBench) {
        // BENCH PLAYER TO MAIN SQUAD: Use auto-slotting to place bench player, remove from bench
        const playerInIndex = updatedPlayers.findIndex((p) => p.playerId === transferData.playerIn);
        if (playerInIndex === -1) {
          throw new Error('Transfer failed: The bench player is no longer available. Please refresh and try again.');
        }

        const playerLeaving = updatedPlayers[playerOutIndex];
        let playerRole: 'captain' | 'viceCaptain' | 'xFactor' | 'regular' = 'regular';
        if (playerLeaving.playerId === existingSquad.captainId) playerRole = 'captain';
        else if (playerLeaving.playerId === existingSquad.viceCaptainId) playerRole = 'viceCaptain';
        else if (playerLeaving.playerId === existingSquad.xFactorId) playerRole = 'xFactor';

        additionalBankedPoints = calculatePlayerContribution(playerLeaving, playerRole);

        const benchPlayer = updatedPlayers[playerInIndex];

        // Reset pointsAtJoining for bench player being promoted
        benchPlayer.pointsAtJoining = benchPlayer.points;

        const mainSquad = updatedPlayers.slice(0, league.squadSize);
        const benchSection = updatedPlayers.slice(league.squadSize);

        // Remove the promoted player from bench
        const updatedBench = benchSection.filter((p) => p.playerId !== transferData.playerIn);

        // Use auto-slotting algorithm on main squad only
        const updatedMainSquad = performAutoSlot(mainSquad, transferData.playerOut, benchPlayer, league);

        updatedPlayers = [...updatedMainSquad, ...updatedBench];

        // AUTO-ASSIGN roles if the outgoing player had C/VC/X
        if (playerRole === 'captain') {
          updatedCaptainId = benchPlayer.playerId;
          benchPlayer.pointsAtJoining = benchPlayer.points;
          benchPlayer.pointsWhenRoleAssigned = benchPlayer.points;
        } else if (playerRole === 'viceCaptain') {
          updatedViceCaptainId = benchPlayer.playerId;
          benchPlayer.pointsAtJoining = benchPlayer.points;
          benchPlayer.pointsWhenRoleAssigned = benchPlayer.points;
        } else if (playerRole === 'xFactor') {
          updatedXFactorId = benchPlayer.playerId;
          benchPlayer.pointsAtJoining = benchPlayer.points;
          benchPlayer.pointsWhenRoleAssigned = benchPlayer.points;
        }
      } else {
        // POOL PLAYER: Replace with a new player from outside the squad
        const playerLeaving = updatedPlayers[playerOutIndex];
        let playerRole: 'captain' | 'viceCaptain' | 'xFactor' | 'regular' = 'regular';
        if (playerLeaving.playerId === existingSquad.captainId) playerRole = 'captain';
        else if (playerLeaving.playerId === existingSquad.viceCaptainId) playerRole = 'viceCaptain';
        else if (playerLeaving.playerId === existingSquad.xFactorId) playerRole = 'xFactor';

        additionalBankedPoints = calculatePlayerContribution(playerLeaving, playerRole);

        const incomingPlayer = availablePlayers.find((p) => p.id === transferData.playerIn);
        if (!incomingPlayer) {
          throw new Error('Transfer failed: The player you selected is no longer available. Please refresh and try again.');
        }

        const newSquadPlayer = makeTransferSquadPlayer({
          playerId: incomingPlayer.id,
          playerName: incomingPlayer.name,
          team: incomingPlayer.team,
          role: incomingPlayer.role,
          points: incomingPlayer.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm || 0,
          isOverseas: incomingPlayer.isOverseas,
        });

        const mainSquad = updatedPlayers.slice(0, league.squadSize);
        const benchSection = updatedPlayers.slice(league.squadSize);

        const updatedMainSquad = performAutoSlot(mainSquad, transferData.playerOut, newSquadPlayer, league);

        updatedPlayers = [...updatedMainSquad, ...benchSection];

        // AUTO-ASSIGN roles if the outgoing player had C/VC/X
        if (playerRole === 'captain') {
          updatedCaptainId = newSquadPlayer.playerId;
          newSquadPlayer.pointsAtJoining = newSquadPlayer.points;
          newSquadPlayer.pointsWhenRoleAssigned = newSquadPlayer.points;
        } else if (playerRole === 'viceCaptain') {
          updatedViceCaptainId = newSquadPlayer.playerId;
          newSquadPlayer.pointsAtJoining = newSquadPlayer.points;
          newSquadPlayer.pointsWhenRoleAssigned = newSquadPlayer.points;
        } else if (playerRole === 'xFactor') {
          updatedXFactorId = newSquadPlayer.playerId;
          newSquadPlayer.pointsAtJoining = newSquadPlayer.points;
          newSquadPlayer.pointsWhenRoleAssigned = newSquadPlayer.points;
        }
      }
    }
  } else if (transferData.changeType === 'roleReassignment') {
    // ROLE REASSIGNMENT WITH AUTOMATIC SWAPPING.
    // INVARIANT for every role change: bank old role contribution, reassign,
    // then reset BOTH baselines (pointsAtJoining + pointsWhenRoleAssigned) to
    // prevent a basePoints leak. See SCORING_LOGIC notes in the original page.

    // ═══ CAPTAIN REASSIGNMENT ═══
    if (transferData.newCaptainId && transferData.newCaptainId !== existingSquad.captainId) {
      if (existingSquad.captainId) {
        const oldCaptain = updatedPlayers.find((p) => p.playerId === existingSquad.captainId);
        if (oldCaptain) {
          additionalBankedPoints += calculatePlayerContribution(oldCaptain, 'captain');
          oldCaptain.pointsAtJoining = oldCaptain.points;
          oldCaptain.pointsWhenRoleAssigned = oldCaptain.points;
        }
      }

      if (transferData.newCaptainId === existingSquad.viceCaptainId) {
        const playerBecomingCaptain = updatedPlayers.find((p) => p.playerId === transferData.newCaptainId);
        if (playerBecomingCaptain) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingCaptain, 'viceCaptain');
        }
        if (existingSquad.captainId) {
          updatedViceCaptainId = existingSquad.captainId;
          const oldCaptainBecomingVC = updatedPlayers.find((p) => p.playerId === existingSquad.captainId);
          if (oldCaptainBecomingVC) {
            oldCaptainBecomingVC.pointsAtJoining = oldCaptainBecomingVC.points;
            oldCaptainBecomingVC.pointsWhenRoleAssigned = oldCaptainBecomingVC.points;
          }
        }
      } else if (transferData.newCaptainId === existingSquad.xFactorId) {
        const playerBecomingCaptain = updatedPlayers.find((p) => p.playerId === transferData.newCaptainId);
        if (playerBecomingCaptain) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingCaptain, 'xFactor');
        }
        if (existingSquad.captainId) {
          updatedXFactorId = existingSquad.captainId;
          const oldCaptainBecomingX = updatedPlayers.find((p) => p.playerId === existingSquad.captainId);
          if (oldCaptainBecomingX) {
            oldCaptainBecomingX.pointsAtJoining = oldCaptainBecomingX.points;
            oldCaptainBecomingX.pointsWhenRoleAssigned = oldCaptainBecomingX.points;
          }
        }
      } else {
        const playerBecomingCaptain = updatedPlayers.find((p) => p.playerId === transferData.newCaptainId);
        if (playerBecomingCaptain) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingCaptain, 'regular');
        }
      }

      const newCaptain = updatedPlayers.find((p) => p.playerId === transferData.newCaptainId);
      if (newCaptain) {
        newCaptain.pointsAtJoining = newCaptain.points;
        newCaptain.pointsWhenRoleAssigned = newCaptain.points;
      }

      updatedCaptainId = transferData.newCaptainId;
    }

    // ═══ VICE-CAPTAIN REASSIGNMENT ═══
    if (transferData.newViceCaptainId && transferData.newViceCaptainId !== existingSquad.viceCaptainId) {
      if (existingSquad.viceCaptainId) {
        const oldVC = updatedPlayers.find((p) => p.playerId === existingSquad.viceCaptainId);
        if (oldVC) {
          additionalBankedPoints += calculatePlayerContribution(oldVC, 'viceCaptain');
          oldVC.pointsAtJoining = oldVC.points;
          oldVC.pointsWhenRoleAssigned = oldVC.points;
        }
      }

      if (transferData.newViceCaptainId === existingSquad.captainId) {
        const playerBecomingVC = updatedPlayers.find((p) => p.playerId === transferData.newViceCaptainId);
        if (playerBecomingVC) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingVC, 'captain');
        }
        if (existingSquad.viceCaptainId) {
          updatedCaptainId = existingSquad.viceCaptainId;
          const oldVCBecomingCaptain = updatedPlayers.find((p) => p.playerId === existingSquad.viceCaptainId);
          if (oldVCBecomingCaptain) {
            oldVCBecomingCaptain.pointsAtJoining = oldVCBecomingCaptain.points;
            oldVCBecomingCaptain.pointsWhenRoleAssigned = oldVCBecomingCaptain.points;
          }
        }
      } else if (transferData.newViceCaptainId === existingSquad.xFactorId) {
        const playerBecomingVC = updatedPlayers.find((p) => p.playerId === transferData.newViceCaptainId);
        if (playerBecomingVC) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingVC, 'xFactor');
        }
        if (existingSquad.viceCaptainId) {
          updatedXFactorId = existingSquad.viceCaptainId;
          const oldVCBecomingX = updatedPlayers.find((p) => p.playerId === existingSquad.viceCaptainId);
          if (oldVCBecomingX) {
            oldVCBecomingX.pointsAtJoining = oldVCBecomingX.points;
            oldVCBecomingX.pointsWhenRoleAssigned = oldVCBecomingX.points;
          }
        }
      } else {
        const playerBecomingVC = updatedPlayers.find((p) => p.playerId === transferData.newViceCaptainId);
        if (playerBecomingVC) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingVC, 'regular');
        }
      }

      const newVC = updatedPlayers.find((p) => p.playerId === transferData.newViceCaptainId);
      if (newVC) {
        newVC.pointsAtJoining = newVC.points;
        newVC.pointsWhenRoleAssigned = newVC.points;
      }

      updatedViceCaptainId = transferData.newViceCaptainId;
    }

    // ═══ X-FACTOR REASSIGNMENT ═══
    if (transferData.newXFactorId && transferData.newXFactorId !== existingSquad.xFactorId) {
      if (existingSquad.xFactorId) {
        const oldX = updatedPlayers.find((p) => p.playerId === existingSquad.xFactorId);
        if (oldX) {
          additionalBankedPoints += calculatePlayerContribution(oldX, 'xFactor');
          oldX.pointsAtJoining = oldX.points;
          oldX.pointsWhenRoleAssigned = oldX.points;
        }
      }

      if (transferData.newXFactorId === existingSquad.captainId) {
        const playerBecomingX = updatedPlayers.find((p) => p.playerId === transferData.newXFactorId);
        if (playerBecomingX) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingX, 'captain');
        }
        if (existingSquad.xFactorId) {
          updatedCaptainId = existingSquad.xFactorId;
          const oldXBecomingCaptain = updatedPlayers.find((p) => p.playerId === existingSquad.xFactorId);
          if (oldXBecomingCaptain) {
            oldXBecomingCaptain.pointsAtJoining = oldXBecomingCaptain.points;
            oldXBecomingCaptain.pointsWhenRoleAssigned = oldXBecomingCaptain.points;
          }
        }
      } else if (transferData.newXFactorId === existingSquad.viceCaptainId) {
        const playerBecomingX = updatedPlayers.find((p) => p.playerId === transferData.newXFactorId);
        if (playerBecomingX) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingX, 'viceCaptain');
        }
        if (existingSquad.xFactorId) {
          updatedViceCaptainId = existingSquad.xFactorId;
          const oldXBecomingVC = updatedPlayers.find((p) => p.playerId === existingSquad.xFactorId);
          if (oldXBecomingVC) {
            oldXBecomingVC.pointsAtJoining = oldXBecomingVC.points;
            oldXBecomingVC.pointsWhenRoleAssigned = oldXBecomingVC.points;
          }
        }
      } else {
        const playerBecomingX = updatedPlayers.find((p) => p.playerId === transferData.newXFactorId);
        if (playerBecomingX) {
          additionalBankedPoints += calculatePlayerContribution(playerBecomingX, 'regular');
        }
      }

      const newX = updatedPlayers.find((p) => p.playerId === transferData.newXFactorId);
      if (newX) {
        newX.pointsAtJoining = newX.points;
        newX.pointsWhenRoleAssigned = newX.points;
      }

      updatedXFactorId = transferData.newXFactorId;
    }
  }

  // VALIDATION: Check minimum squad requirements (player substitutions only)
  if (transferData.changeType === 'playerSubstitution') {
    const mainSquad = updatedPlayers.slice(0, league.squadSize);
    const roleCounts = {
      batsman: mainSquad.filter((p) => p.role === 'batsman').length,
      bowler: mainSquad.filter((p) => p.role === 'bowler').length,
      allrounder: mainSquad.filter((p) => p.role === 'allrounder').length,
      wicketkeeper: mainSquad.filter((p) => p.role === 'wicketkeeper').length,
    };

    const violations: string[] = [];

    if (roleCounts.batsman < league.squadRules.minBatsmen) {
      violations.push(`This transfer would leave you with only ${roleCounts.batsman} batsman${roleCounts.batsman !== 1 ? 's' : ''} (minimum ${league.squadRules.minBatsmen} required)`);
    }
    if (roleCounts.bowler < league.squadRules.minBowlers) {
      violations.push(`This transfer would leave you with only ${roleCounts.bowler} bowler${roleCounts.bowler !== 1 ? 's' : ''} (minimum ${league.squadRules.minBowlers} required)`);
    }
    if (roleCounts.allrounder < league.squadRules.minAllrounders) {
      violations.push(`This transfer would leave you with only ${roleCounts.allrounder} allrounder${roleCounts.allrounder !== 1 ? 's' : ''} (minimum ${league.squadRules.minAllrounders} required)`);
    }
    if (roleCounts.wicketkeeper < league.squadRules.minWicketkeepers) {
      violations.push(`This transfer would leave you with only ${roleCounts.wicketkeeper} wicketkeeper${roleCounts.wicketkeeper !== 1 ? 's' : ''} (minimum ${league.squadRules.minWicketkeepers} required)`);
    }

    // Check overseas player limit if enabled
    if (league.squadRules.overseasPlayersEnabled) {
      const maxOverseas = league.squadRules.maxOverseasPlayers || 4;
      const overseasCount = countOverseasInMainSquad(
        mainSquad,
        availablePlayers.map((p) => ({ id: p.id, isOverseas: p.isOverseas ?? false })),
      );
      if (overseasCount > maxOverseas) {
        violations.push(`This transfer would give you ${overseasCount} overseas players (maximum ${maxOverseas} allowed)`);
      }
    }

    if (violations.length > 0) {
      throw new Error(violations.join('. '));
    }
  }

  // Calculate new banked points total
  const newBankedPoints = (existingSquad.bankedPoints || 0) + additionalBankedPoints;

  // Pre-transfer snapshot for rollback capability
  const preTransferSnapshot = {
    players: JSON.parse(JSON.stringify(existingSquad.players)) as SquadPlayer[],
    captainId: existingSquad.captainId || null,
    viceCaptainId: existingSquad.viceCaptainId || null,
    xFactorId: existingSquad.xFactorId || null,
    bankedPoints: existingSquad.bankedPoints || 0,
    totalPoints: 0, // filled below
    timestamp: new Date(),
  };

  const oldCalculatedPoints = calculateSquadPoints(
    existingSquad.players,
    existingSquad.captainId || null,
    existingSquad.viceCaptainId || null,
    existingSquad.xFactorId || null,
    existingSquad.bankedPoints || 0,
  );
  preTransferSnapshot.totalPoints = oldCalculatedPoints.totalPoints;

  const calculatedPoints = calculateSquadPoints(
    updatedPlayers,
    updatedCaptainId || null,
    updatedViceCaptainId || null,
    updatedXFactorId || null,
    newBankedPoints,
  );

  // CRITICAL VALIDATION (PT-001): For bench transfers, points should be stable
  if (transferData.transferType === 'bench') {
    const pointsDifference = Math.abs(calculatedPoints.totalPoints - oldCalculatedPoints.totalPoints);
    // Increased tolerance to 1.0 to account for floating-point rounding
    if (pointsDifference > 1.0) {
      console.error('🚨 BENCH TRANSFER POINT STABILITY VIOLATION 🚨');
      console.error(`Old Total: ${oldCalculatedPoints.totalPoints}`);
      console.error(`New Total: ${calculatedPoints.totalPoints}`);
      console.error(`Difference: ${calculatedPoints.totalPoints - oldCalculatedPoints.totalPoints}`);
      console.error(`Old Banked: ${existingSquad.bankedPoints || 0}`);
      console.error(`New Banked: ${newBankedPoints}`);
      console.error(`Additional Banked: ${additionalBankedPoints}`);
      console.error('Player Out:', transferData.playerOut);
      console.error('Player In:', transferData.playerIn);

      // Log detailed player breakdown
      console.error('Starting XI after transfer:');
      const startingXI = updatedPlayers.slice(0, league.squadSize);
      startingXI.forEach((p, idx) => {
        console.error(`  ${idx + 1}. ${p.playerName}: points=${p.points}, atJoining=${p.pointsAtJoining}, whenRole=${p.pointsWhenRoleAssigned}`);
      });

      throw new Error(
        'SYSTEM_ERROR:POINT_STABILITY:A point calculation error was detected. Your squad has not been changed. Please contact the league admin with error code PT-001.',
      );
    }
  }

  // CRITICAL VALIDATION (PT-002): For role reassignments, points should also be stable.
  // Role swaps should only move points between banked/active, not change total.
  if (transferData.changeType === 'roleReassignment') {
    const pointsDifference = Math.abs(calculatedPoints.totalPoints - oldCalculatedPoints.totalPoints);
    // Increased tolerance to 1.0 to account for floating-point rounding
    if (pointsDifference > 1.0) {
      console.error('🚨 ROLE REASSIGNMENT POINT STABILITY VIOLATION 🚨');
      console.error(`Old Total: ${oldCalculatedPoints.totalPoints}`);
      console.error(`New Total: ${calculatedPoints.totalPoints}`);
      console.error(`Difference: ${calculatedPoints.totalPoints - oldCalculatedPoints.totalPoints}`);
      console.error(`Old Banked: ${existingSquad.bankedPoints || 0}`);
      console.error(`New Banked: ${newBankedPoints}`);
      console.error(`Additional Banked: ${additionalBankedPoints}`);
      console.error('Old Captain:', existingSquad.captainId);
      console.error('New Captain:', transferData.newCaptainId);
      console.error('Old VC:', existingSquad.viceCaptainId);
      console.error('New VC:', transferData.newViceCaptainId);
      console.error('Old X-Factor:', existingSquad.xFactorId);
      console.error('New X-Factor:', transferData.newXFactorId);

      // Log detailed role breakdown
      console.error('Role changes:');
      const oldC = existingSquad.players.find((p) => p.playerId === existingSquad.captainId);
      const oldVC = existingSquad.players.find((p) => p.playerId === existingSquad.viceCaptainId);
      const oldX = existingSquad.players.find((p) => p.playerId === existingSquad.xFactorId);
      const newC = updatedPlayers.find((p) => p.playerId === updatedCaptainId);
      const newVC = updatedPlayers.find((p) => p.playerId === updatedViceCaptainId);
      const newX = updatedPlayers.find((p) => p.playerId === updatedXFactorId);

      console.error(`  Old C: ${oldC?.playerName} (pts=${oldC?.points}, whenRole=${oldC?.pointsWhenRoleAssigned})`);
      console.error(`  New C: ${newC?.playerName} (pts=${newC?.points}, whenRole=${newC?.pointsWhenRoleAssigned})`);
      console.error(`  Old VC: ${oldVC?.playerName} (pts=${oldVC?.points}, whenRole=${oldVC?.pointsWhenRoleAssigned})`);
      console.error(`  New VC: ${newVC?.playerName} (pts=${newVC?.points}, whenRole=${newVC?.pointsWhenRoleAssigned})`);
      console.error(`  Old X: ${oldX?.playerName} (pts=${oldX?.points}, whenRole=${oldX?.pointsWhenRoleAssigned})`);
      console.error(`  New X: ${newX?.playerName} (pts=${newX?.points}, whenRole=${newX?.pointsWhenRoleAssigned})`);

      throw new Error(
        'SYSTEM_ERROR:ROLE_REASSIGNMENT:A role banking error was detected. Your squad has not been changed. Please contact the league admin with error code PT-002.',
      );
    }
  }

  // Build the transfer history entry (only include defined values)
  const transferHistoryEntry: TransferHistoryEntry = {
    timestamp: new Date(),
    transferType: transferData.transferType,
    changeType: transferData.changeType,
  };

  // NOTE: matches the original page behaviour exactly, including the latent
  // omission of newCaptainId from the history entry (captain-only reassignments
  // are recorded with changeType 'roleReassignment' but no role-id field).
  if (transferData.playerOut) transferHistoryEntry.playerOut = transferData.playerOut;
  if (transferData.playerIn) transferHistoryEntry.playerIn = transferData.playerIn;
  if (transferData.newViceCaptainId) transferHistoryEntry.newViceCaptainId = transferData.newViceCaptainId;
  if (transferData.newXFactorId) transferHistoryEntry.newXFactorId = transferData.newXFactorId;

  transferHistoryEntry.bankedAmount = additionalBankedPoints;
  transferHistoryEntry.totalBankedAfter = newBankedPoints;
  transferHistoryEntry.pointsBefore = oldCalculatedPoints.totalPoints;
  transferHistoryEntry.pointsAfter = calculatedPoints.totalPoints;
  transferHistoryEntry.preTransferSnapshot = preTransferSnapshot;

  // Admin-on-behalf audit stamp (only when an admin acted for the owner).
  if (actedByAdminUid !== undefined) {
    transferHistoryEntry.actedByAdminUid = actedByAdminUid;
  }

  // Clean up players array before saving (Firebase strips undefined fields).
  const cleanedPlayers = updatedPlayers.map((player) => {
    const cleanPlayer: Record<string, unknown> = {
      playerId: player.playerId,
      playerName: player.playerName,
      team: player.team,
      role: player.role,
      points: player.points,
      pointsAtJoining: player.pointsAtJoining ?? 0,
    };
    if (player.pointsWhenRoleAssigned !== undefined) {
      cleanPlayer.pointsWhenRoleAssigned = player.pointsWhenRoleAssigned;
    }
    cleanPlayer.isOverseas = player.isOverseas ?? false;
    return cleanPlayer;
  });

  const updatePayload: Record<string, unknown> = {
    players: cleanedPlayers,
    totalPoints: calculatedPoints.totalPoints,
    captainPoints: calculatedPoints.captainPoints,
    viceCaptainPoints: calculatedPoints.viceCaptainPoints,
    xFactorPoints: calculatedPoints.xFactorPoints,
    bankedPoints: newBankedPoints,
    transfersUsed: (existingSquad.transfersUsed || 0) + 1,
    benchTransfersUsed:
      transferData.transferType === 'bench'
        ? (existingSquad.benchTransfersUsed || 0) + 1
        : (existingSquad.benchTransfersUsed || 0),
    flexibleTransfersUsed:
      transferData.transferType === 'flexible'
        ? (existingSquad.flexibleTransfersUsed || 0) + 1
        : (existingSquad.flexibleTransfersUsed || 0),
    midSeasonTransfersUsed:
      transferData.transferType === 'midSeason'
        ? (existingSquad.midSeasonTransfersUsed || 0) + 1
        : (existingSquad.midSeasonTransfersUsed || 0),
    wildcardTransfersUsed:
      transferData.transferType === 'wildcard'
        ? (existingSquad.wildcardTransfersUsed ?? 0) + 1
        : (existingSquad.wildcardTransfersUsed ?? 0),
    transferHistory: [...(existingSquad.transferHistory || []), transferHistoryEntry],
    lastUpdated: new Date(),
  };

  if (updatedCaptainId) {
    updatePayload.captainId = updatedCaptainId;
  }
  if (updatedViceCaptainId) {
    updatePayload.viceCaptainId = updatedViceCaptainId;
  }
  if (updatedXFactorId) {
    updatePayload.xFactorId = updatedXFactorId;
  }

  return {
    updatePayload,
    pointsBefore: oldCalculatedPoints.totalPoints,
    pointsAfter: calculatedPoints.totalPoints,
    bankedDelta: additionalBankedPoints,
  };
}
